import { checkLogin } from '@/api'
import log from '@/log'
import types from '@/types'
import store from '@/store'
import { ALLOWED_ACCESS } from '@/types/company'
import Vue from 'vue'
import Router, { RawLocation, Route } from 'vue-router'
import { ConfigModel } from './types/config'
import { setStorage, getStorage } from '@/helpers/storageProxy'
import {
  isUserPlatformRoute,
  notifyUserPlatformRouter,
  removeSalesDashboardAppCss,
} from '@/helpers/userPlatformRoutes'

Vue.use(Router)

// This is how we define prototype types in Typescript
declare module 'vue-router/types/router' {
  interface VueRouter {
    /**
     * Prototype method to do a custom redirect
     */
    $doRedirect: (redirect: string | RawLocation, onComplete?: () => void, onAbort?: (err: Error) => void) => void
    /**
     * Handle a redirect query parameter, optionally adding a message/error
     */
    $handleRedirectQuery: () => void
    /**
     * Get the given query param
     */
    $queryParam: (name: string, join?: string) => string
    /**
     * Set the given query param
     */
    $setQueryParam: (name: string, value: string) => void
  }
}

/**
 * Do a redirect to the given redirect.  If the redirect is a string
 * and contains a '/', it assumes its a path, else it assumes its a route name
 */
Router.prototype.$doRedirect = (
  redirect: string | RawLocation,
  onComplete?: () => void,
  onAbort?: (err: Error) => void
) => {
  if (!redirect) {
    log.error('No redirect given to $doRedirect! Redirecting to home')
    redirect = 'home'
  }

  if (typeof redirect === 'string') {
    if (redirect?.includes('/')) {
      redirect = { path: redirect }
    } else {
      redirect = { name: redirect }
    }
  }

  router.push(redirect, onComplete, onAbort)
}

/**
 * Look for a query param called "redirect" and redirect to it
 */
Router.prototype.$handleRedirectQuery = () => {
  const redirect = Router.prototype.$queryParam('redirect')
  if (!redirect) return

  Router.prototype.$doRedirect(redirect)
}

/**
 * Get a query parameter as a string. If it's a list and there is a join param,
 * join it and return it. Else just take the first item in the list. If it doesn't
 * exist, it returns empty string
 */
Router.prototype.$queryParam = (name: string, join: string = ''): string => {
  const query = router.currentRoute.query
  const val = query[name]
  if (!val) return ''
  if (typeof val === 'string') {
    return decodeURIComponent(val)
  }

  if (!val.length) return ''
  if (val.length === 1) return decodeURIComponent(val[0] ?? '')
  return decodeURIComponent(val.join(join))
}

/**
 * Set a query parameter on the current URL, and do not refresh the page
 */
Router.prototype.$setQueryParam = (name: string, value: string) => {
  const query = Object.assign({}, router.currentRoute.query)
  query[name] = encodeURIComponent(value)
  window.history.pushState(
    {},
    '',
    router.resolve({
      name: router.currentRoute.name || '',
      query,
    }).href
  )
  // Can't get the router pushstate to work for some reason, so I have to
  // resolve this myself and use window.history.pushState directly
  // router.push(
  //   {
  //     name: router.currentRoute.name,
  //     query
  //   },
  //   (s: any) => {
  //     log.debug('Pushed new state: ', s)
  //     return true
  //   }
  // )
}

const configModel = new ConfigModel()

const router = new Router({
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
  routes: [
    {
      name: 'home',
      path: '/',
      redirect: { name: 'ihmCampaignSelection' },
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.isIHMBranded() || types.company.isUnifiedUser()) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/CommandCenterDashboard'),
      name: 'commandCenterDashboard',
      path: '/dashboards/home',
      meta: { title: 'Command Center Dashboard' },
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.isIHMBranded() || types.company.isUnifiedUser()) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/Campaign'),
      name: 'ihmCampaignReports',
      path: '/dashboards/campaign-reports',
      alias: '/dashboards/ihm-campaign-reports',
      meta: { title: 'Campaign Reports' },
    },

    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.isIHMBranded() || types.company.isUnifiedUser()) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/CampaignRedirect'),
      name: 'ihmCampaignReportsRedirect',
      path: '/dashboards/redirect/ihm-campaign-reports',
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (
          (types.company.hasDarklyFlag() && (types.company.isIHMBranded() || types.company.isUnifiedUser())) ||
          ((types.company.isIHMBranded() || types.company.isUnifiedUser()) && types.company.isCompanyDashboardUser())
        ) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/Company'),
      name: 'ihmCompanyReports',
      path: '/dashboards/company-reports',
      alias: '/dashboards/ihm-company-reports',
      meta: { title: 'Company Reports' },
    },

    /**
     * Integration Dashboards
     */
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.isIHMBranded() || types.company.isUnifiedUser()) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/IntegrationCampaign'),
      name: 'integrationIHMCampaignReports',
      path: '/dashboards/integration/ihm-campaign-reports',
      meta: { title: 'Integration Campaign Reports' },
    },
    /**
     * Integration Dashboards
     */
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.isIHMBranded() || types.company.isUnifiedUser()) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/CustomerOrder'),
      name: 'integrationCustomerOrderReports',
      path: '/dashboards/integration/customer-order-reports',
      meta: { title: 'Customer Order Reports' },
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (
          (types.company.hasDarklyFlag() && (types.company.isIHMBranded() || types.company.isUnifiedUser())) ||
          ((types.company.isIHMBranded() || types.company.isUnifiedUser()) && types.company.isCompanyDashboardUser())
        ) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/IntegrationCompany'),
      name: 'integrationIHMCompanyReports',
      path: '/dashboards/integration/ihm-company-reports',
      meta: { title: 'Integration Company Reports' },
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.isIHMBranded() || types.company.isUnifiedUser()) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/IntegrationIHMCampaignSelection'),
      name: 'integrationIHMCampaignSelection',
      path: '/dashboards/integration/ihm-campaign-selection',
      meta: { title: 'Integration Campaign Selection' },
    },

    /**
     * Bundle Dashboard
     */

    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.isIHMBranded() || types.company.isUnifiedUser()) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/Bundle'),
      name: 'bundleDashboard',
      path: '/dashboards/bundle/:id',
      alias: '/dashboards/ihm-bundle/:id',
      meta: { title: 'Bundle Dashboard' },
    },

    /**
     * ArtsAi creative
     */
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.isIHMBranded() || types.company.isUnifiedUser()) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('artsaiCreative'),
      name: 'artsaiCreative',
      path: '/dashboards/artsai/preview',
      meta: { title: 'ArtsAI Creative' },
    },

    {
      beforeEnter: ensureAuthed,
      component: lazyLoadView('dashboards/SisenseDashboard'),
      name: 'sisenseDashboard',
      path: '/dashboards/custom/:id',
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.hasAccess(ALLOWED_ACCESS.TAB_EXPLORER)) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/ExplorerDashboard'),
      name: 'explorerDashboard',
      path: '/dashboards/explorer',
      alias: '/dashboards/explorer/explorer/*',
      meta: { title: 'Explorer Dashboard' },
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.hasAccess(ALLOWED_ACCESS.TAB_PACING)) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/PacingDashboard'),
      name: 'pacingDashboard',
      path: '/dashboards/pacing',
      alias: '/dashboards/pacing/pacing/*',
      meta: { title: 'Pacing Dashboard' },
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.hasAccess(ALLOWED_ACCESS.AGENCY_LEADERSHIP_DASHBOARD_ACCESS)) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/AgencyLeadershipDashboard'),
      name: 'agencyLeadershipDashboard',
      path: '/dashboards/agency-dashboard',
      alias: [
        '/agency-dashboard/agency-leadership-dashboard/*',
        '/dashboards/agency-leadership',
        '/dashboards/agency-leadership/agency-leadership-dashboard/*',
        '/agency-leadership-dashboard',
      ],
      meta: { title: 'Agency Spend Planner' },
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.hasAccess(ALLOWED_ACCESS.TAB_IHM_REPORTS)) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/IHMReport'),
      name: 'ihmReport',
      path: '/dashboards/ihm-report',
      alias: '/dashboards/ihm-report/ihm-template/*',
      meta: { title: 'Reports' },
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        ensureAuthed(to, from, next)
        const showBeta = to.query?.show_beta
        log.debug('request to show UI ', showBeta)
        log.debug('LDFlag allow show UI ', store.getters.checkHasAccessTo('violation-dashboard-beta'))
        if (showBeta != undefined && showBeta != '') {
          const shouldEnable =
            showBeta === 'true' ||
            showBeta === '1' ||
            (Array.isArray(showBeta) && (showBeta[0] === 'true' || showBeta[0] === '1'))
          setStorage('show_beta', `${shouldEnable}`)
          const nextQuery = { ...to.query }
          // const nextMeta = from.meta || {}
          delete (nextQuery as any).show_beta
          next({
            name: to.name || undefined,
            path: to.name ? undefined : to.path,
            params: to.params,
            // meta: nextMeta,
            query: undefined,
            hash: to.hash,
            replace: true,
          })
          return
        }
        next()
      },
      component: () => {
        const showBeta = getStorage('show_beta') === true
        log.debug('showBeta in storage ', showBeta)
        return showBeta
          ? import('@/views/dashboards/ViolationDashboardV2.vue')
          : import('@/views/dashboards/ViolationDashboard.vue')
      },
      name: 'violationDashboard',
      path: '/dashboards/violation',
      alias: '/dashboards/violation/violation/*',
      meta: { title: 'Violations Dashboard' },
    },
    {
      // Access is behind the spotlight-in-ihm-dash LD flag, checked in the view
      // because the flag lookup is async.
      beforeEnter: ensureAuthed,
      component: lazyLoadView('dashboards/SpotlightDashboard'),
      name: 'spotlightDashboard',
      // Optional param: the account can also arrive as ?sf_account_id=.
      path: '/dashboards/spotlight/:sfAccountId?',
      meta: { title: 'Spotlight Dashboard' },
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.isIHMBranded() || types.company.isUnifiedUser()) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/IHMCampaignSelection'),
      name: 'ihmCampaignSelection',
      path: '/dashboards/campaign-selection',
      alias: '/dashboards/ihm-campaign-selection',
      meta: { title: 'Campaign Selection' },
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.isIHMBranded() || types.company.isUnifiedUser()) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('dashboards/SelectionNew'),
      name: 'selectionNew',
      path: '/dashboards-migrating/campaign-selection',
      alias: '/dashboards-migrating/ihm-campaign-selection',
      meta: { title: 'Campaign Selection' },
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        if (types.company.hasAccess(ALLOWED_ACCESS.TAB_PLANNER)) {
          ensureAuthed(to, from, next)
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      name: 'signaturePlanner',
      path: '/app/planner',
      redirect: '/dashboards/sales/planner',
    },
    {
      children: [
        {
          path: 'lineItems/:id',
          component: lazyLoadView('dashboards/SalesDashboard'),
          name: 'salesLineItem',
        },
        {
          path: 'violations',
          component: lazyLoadView('dashboards/SalesDashboard'),
          name: 'salesViolations',
        },
        {
          path: 'planner',
          component: lazyLoadView('dashboards/SalesDashboard'),
          name: 'salesPlanner',
        },
      ],
      component: lazyLoadView('dashboards/SalesDashboard'),
      name: 'salesDashboard',
      path: '/dashboards/sales',
      meta: { title: 'Sales Dashboard' },
    },
    {
      children: [
        {
          path: 'metadataschema',
          component: lazyLoadView('dashboards/SalesDashboard'),
          name: 'dealMetadataSchema',
          meta: { title: 'Metadata Schema' },
        },
        {
          path: 'deals',
          component: lazyLoadView('dashboards/SalesDashboard'),
          name: 'dealDashboard',
          meta: { title: 'Deal Dashboard' },
        },
        {
          path: 'deals/:dealId',
          component: lazyLoadView('dashboards/SalesDashboard'),
          name: 'dealDetail',
          meta: { title: 'Deal Dashboard' },
        },
      ],
      component: lazyLoadView('dashboards/SalesDashboard'),
      name: 'adminDashboard',
      path: '/dashboards/admin',
      meta: { title: 'Admin Dashboard' },
    },
    {
      path: '/admin/optimizer/tools/bulk_setting_update',
      name: 'admin-bulk-tools',
      component: () => import('@/views/dashboards/OptimizerDashboard.vue'),
      beforeEnter: (to: Route, from: Route, next: any): void => {
        ensureAuthed(to, from, next)
      },
    },
    {
      path: '/optimizer*',
      name: 'optimizer-dashboard',
      component: () => import('@/views/dashboards/OptimizerDashboard.vue'),
      beforeEnter: (to: Route, from: Route, next: any): void => {
        ensureAuthed(to, from, next)
      },
    },
    {
      component: lazyLoadView('Error'),
      name: 'error',
      path: '/error',
    },
    {
      component: lazyLoadView('Placeholder'),
      name: 'placeholder',
      path: '/placeholder',
    },
    {
      component: lazyLoadView('Adgroup'),
      name: 'adgroup',
      path: '/creative/:id',
    },
    {
      component: lazyLoadView('Creative'),
      name: 'creative',
      path: '/new/preview',
    },
    {
      component: lazyLoadView('NotFoundArchived'),
      name: 'notFoundArchived',
      path: '/not-found-archived',
    },
    {
      name: 'googleAnalyticsOnboarding',
      path: '/dashboards/onboarding/google-analytics',
      beforeEnter: (to, from) => {
        if (types.company.isIHMBranded() || types.company.isUnifiedUser()) {
          ensureAuthed(to, from, () => {
            const authToken = types.user.authToken()
            if (!authToken) {
              log.error('No auth token for GA onboarding')
              router.push({ name: 'error' })
              return
            }

            const baseUrl = configModel.DataManagementDomain()
            const redirectUrl = `${baseUrl}/#/connect/googleanalytics`
            const params = new URLSearchParams({
              redirect_url: redirectUrl,
              authToken: authToken,
              source: 'ihm-dashboard',
            })
            const finalUrl = `${baseUrl}/oauth/googleanalytics?${params.toString()}`

            window.location.replace(finalUrl)
          })
        } else {
          router.$doRedirect({ name: 'notFound' })
        }
      },
      component: lazyLoadView('Placeholder'),
    },
    {
      beforeEnter: (to: Route, from: Route, next: any): void => {
        ensureAuthed(to, from, next)
      },
      component: lazyLoadView('ProfileSettingsHost'),
      name: 'profileSettings',
      path: '/profile/settings',
      meta: { title: 'Profile Settings' },
    },
    {
      component: lazyLoadView('NotFound'),
      name: 'notFound',
      path: '/*',
    },
  ],
  mode: 'history',
})

router.beforeEach((to, _from, next) => {
  if (isUserPlatformRoute(to.path)) {
    removeSalesDashboardAppCss()
  }
  next()
})

function getRouteTitle(to: Route): string {
  const prefix = types.company.getDashboardTitleBrandPrefix()
  if (to.meta && typeof to.meta.title === 'string' && to.meta.title.trim().length > 0) {
    return `${prefix} ${to.meta.title.trim()}`
  }
  return `${prefix} Dashboards`
}

router.afterEach((to) => {
  if (typeof document !== 'undefined') {
    document.title = getRouteTitle(to)
  }
})

/**
 * A function to ensure the user is logged in before going to the route.
 * If they are not, redirect to login page
 * @returns any - void
 */
function ensureAuthed(to: Route, from: Route, next: any): void {
  checkLogin(to.fullPath)
  next() // Won't get here if login is invalid
}
/**
 * A function used to lazy load an component.
 */
function lazyLoadView(view: any) {
  return () => import(`@/views/${view}.vue`)
}

/**
 * Set Global Navigate
 */
if (typeof window !== 'undefined') {
  window.navigateTo = (path: string) => {
    if (!path) return

    // Strip origin if full URL is passed
    try {
      const url = new URL(path, window.location.origin)
      const relativePath = url.pathname + url.search + url.hash

      const wasUserPlatformRoute = isUserPlatformRoute(router.currentRoute.path)
      const isUserPlatformPath = isUserPlatformRoute(url.pathname)

      if (router.currentRoute.fullPath !== relativePath) {
        router
          .push(relativePath)
          .then(() => {
            if (isUserPlatformPath || wasUserPlatformRoute) {
              notifyUserPlatformRouter()
            }
          })
          .catch((err) => {
            if (err.name !== 'NavigationDuplicated') {
              // eslint-disable-next-line no-console
              console.error('Navigation error:', err)
            }
          })
      } else if (isUserPlatformPath) {
        notifyUserPlatformRouter()
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Invalid navigation path:', path, err)
    }
  }
}

export default router
